// ProductComments.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Rating,
  Stack,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { privateApi } from "../../../api/axiosConfig";
import CommentRating from "./CommentRating";

export default function ProductComments({ productId, onAddComment }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [score, setScore] = useState(5);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const MAX_COMMENT_LENGTH = 2000;

  const maskPhoneNumber = (str) => {
    if (!str) return "کاربر";
    if (/^\d+$/.test(str) && str.length >= 10) {
      return str.replace(/(\d{4})\d+(\d{3})/, "$1***$2");
    }
    return str;
  };

  useEffect(() => {
    if (productId) {
      fetchComments();
    }
  }, [productId]);

  useEffect(() => {
    if (!submitSuccess) return;

    const timer = setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [submitSuccess]);

  useEffect(() => {
    if (!submitError) return;

    const timer = setTimeout(() => {
      setSubmitError(null);
    }, 5000);

    return () => clearTimeout(timer);
  }, [submitError]);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const res = await privateApi.get(
        `/api/v1/products/${productId}/feedbacks/`,
      );
      const data = res.data;
      const confirmedComments = (data.results || []).filter(
        (c) => c.is_confirmed === true,
      );
      setComments(confirmedComments);
    } catch (err) {
      console.error("Error fetching comments:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const trimmedComment = commentText.trim();

    if (!trimmedComment || !productId || submitting) {
      return;
    }

    if (trimmedComment.length > MAX_COMMENT_LENGTH) {
      setSubmitError("متن نظر نمی‌تواند بیشتر از ۲۰۰۰ کاراکتر باشد");
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      const payload = {
        score,
        comment: trimmedComment,
      };

      if (onAddComment) {
        await onAddComment(payload);
      } else {
        await privateApi.post(
          `/api/v1/products/${productId}/feedbacks/`,
          payload,
        );
      }

      setCommentText("");
      setScore(5);
      setSubmitSuccess(true);

      await fetchComments();
    } catch (err) {
      if (process.env.REACT_APP_ENV === "dev") {
        console.error("Error posting comment:", err);
      }

      const errors = err.response?.data;
      const nonFieldErrors = errors?.non_field_errors;

      if (nonFieldErrors?.length) {
        setSubmitError(nonFieldErrors[0]);
      } else if (errors?.comment?.length) {
        setSubmitError(errors.comment[0]);
      } else if (errors?.score?.length) {
        setSubmitError(errors.score[0]);
      } else {
        setSubmitError("خطایی در ثبت نظر رخ داده است");
      }

      // Preserve the user's comment after a failed submission.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 4 },
        borderRadius: 4,
        border: "1px solid",
        borderColor: "grey.200",
        mt: 4,
      }}
    >
      <Typography variant="h6" fontWeight={800} mb={3}>
        نظرات کاربران
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ mb: 4, p: 2.5, bgcolor: "#f9f8fa", borderRadius: 3 }}
      >
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          ثبت نظر جدید
        </Typography>

        {submitSuccess && (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              borderRadius: 2,
            }}
          >
            نظر شما بعد از تاییدیه منتشر می شود
          </Alert>
        )}

        {submitError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              borderRadius: 2,
            }}
          >
            {submitError === "You already submitted a comment on this product"
              ? "شما از قبل نظر خود را ثبت کرده اید"
              : submitError}
          </Alert>
        )}

        <CommentRating score={score} setScore={setScore} />

        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="دیدگاه خود را درباره این محصول بنویسید..."
          value={commentText}
          onChange={(e) => {
            setCommentText(e.target.value);
            if (submitError) setSubmitError(null);
            if (submitSuccess) setSubmitSuccess(false);
          }}
          inputProps={{
            maxLength: MAX_COMMENT_LENGTH,
          }}
          helperText={`${commentText.length}/${MAX_COMMENT_LENGTH}`}
          sx={{
            mb: 2,
            bgcolor: "#fff",
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={submitting || !commentText.trim()}
          sx={{
            bgcolor: "#8257ac",
            "&:hover": { bgcolor: "#6f449a" },
            fontWeight: 700,
            borderRadius: 2,
            px: 4,
          }}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "ثبت نظر"
          )}
        </Button>
      </Box>

      <Divider sx={{ my: 3 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : comments.length === 0 ? (
        <Alert severity="info">هنوز نظری برای این محصول ثبت نشده است.</Alert>
      ) : (
        <Stack spacing={2}>
          {comments.map((item) => (
            <Box
              key={item.id}
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "grey.100",
                borderRadius: 2.5,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "#8257ac",
                      fontSize: "0.85rem",
                    }}
                  >
                    {item.user ? item.user.charAt(0).toUpperCase() : "U"}
                  </Avatar>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{
                      direction: "ltr",
                      unicodeBidi: "plaintext",
                      display: "inline-block",
                    }}
                  >
                    {maskPhoneNumber(item.user)}
                  </Typography>
                </Stack>
                <Rating value={item.score} readOnly size="small" />
              </Stack>

              <Typography
                variant="body2"
                color="text.primary"
                sx={{ mt: 1, lineHeight: 1.7 }}
              >
                {item.comment}
              </Typography>

              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                {new Date(item.created_at).toLocaleDateString("fa-IR")}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Paper>
  );
}
